/**
 * Simulator Rate Limiter
 * Prevents overwhelming the API with requests during simulation
 * Max 5 requests per second (300 per minute)
 */

export class SimulatorRateLimiter {
  private requestCount: number = 0;
  private windowStart: number = Date.now();
  private readonly maxRequestsPerMinute: number;
  private readonly windowMs: number = 60000;
  private requestTimes: number[] = [];

  constructor(maxRequestsPerMinute: number = 300) {
    this.maxRequestsPerMinute = maxRequestsPerMinute;
  }

  /**
   * Throttle requests to stay within rate limits
   * Returns immediately if under limit, otherwise waits
   */
  async throttle(): Promise<void> {
    const now = Date.now();

    // Clean up old request times (older than 1 minute)
    this.requestTimes = this.requestTimes.filter(time => now - time < this.windowMs);

    // Check if we're at the limit
    if (this.requestTimes.length >= this.maxRequestsPerMinute) {
      // Calculate how long to wait
      const oldestRequest = this.requestTimes[0];
      const waitTime = this.windowMs - (now - oldestRequest) + 100; // Add 100ms buffer

      if (waitTime > 0) {
        console.warn(`[RateLimiter] Rate limit reached, waiting ${waitTime}ms`);
        await this.delay(waitTime);

        // Clean up again after waiting
        const afterWait = Date.now();
        this.requestTimes = this.requestTimes.filter(time => afterWait - time < this.windowMs);
      }
    }

    // Record this request
    this.requestTimes.push(Date.now());
  }

  /**
   * Get current requests per second (rolling average)
   */
  getRequestsPerSecond(): number {
    const now = Date.now();
    const recentRequests = this.requestTimes.filter(time => now - time < 1000);
    return recentRequests.length;
  }

  /**
   * Get current rate utilization (0-1)
   */
  getUtilization(): number {
    const now = Date.now();
    const recentRequests = this.requestTimes.filter(time => now - time < this.windowMs);
    return recentRequests.length / this.maxRequestsPerMinute;
  }

  /**
   * Reset the rate limiter
   */
  reset(): void {
    this.requestCount = 0;
    this.windowStart = Date.now();
    this.requestTimes = [];
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Singleton instance for shared rate limiting across all workflows
let sharedRateLimiter: SimulatorRateLimiter | null = null;

export const getSharedRateLimiter = (): SimulatorRateLimiter => {
  if (!sharedRateLimiter) {
    sharedRateLimiter = new SimulatorRateLimiter(300); // 5 req/sec average
  }
  return sharedRateLimiter;
};

export const resetSharedRateLimiter = (): void => {
  if (sharedRateLimiter) {
    sharedRateLimiter.reset();
  }
};
